import { useQuery, useQueryClient } from "@tanstack/react-query";
import { applicantApi, apiBaseConfigured, type ApiError, type VoucherInvitation } from "@/lib/api";

/**
 * Fetches the applicant's voucher invitations. Cached + deduped by
 * React Query so `VoucherInviteForm` and the page-level submission
 * eligibility check can both call this without duplicate network calls.
 *
 * Polls every 12s while the tab is visible so the submission state on
 * the final step updates as vouches come in. Matches the polling cadence
 * of `useSessionPolling` (see docs/CLAUDE-applicant-submission-eligibility.md §8).
 */
export function useVoucherInvitations(token: string | undefined) {
  const enabled = Boolean(token) && apiBaseConfigured();
  const queryClient = useQueryClient();
  const queryKey = ["applicant", "voucher-invitations", token];

  const query = useQuery<VoucherInvitation[]>({
    queryKey,
    queryFn: async () => {
      if (!token) return [];
      try {
        const res = await applicantApi.listVoucherInvitations(token);
        return keepKnownLinks(
          res.invitations ?? [],
          queryClient.getQueryData<VoucherInvitation[]>(queryKey),
        );
      } catch (e) {
        // A 404 means this environment genuinely has no such endpoint (backend
        // not yet deployed) — an empty list is the honest answer.
        //
        // Everything else MUST rethrow. Swallowing a 5xx or a network blip and
        // returning [] writes an empty list into the cache as if the server had
        // said "you have no vouchers", and since this query polls every 12s a
        // single transient failure makes every invitation disappear mid-flow.
        // On a thrown error React Query retains the last successful data, so
        // the list survives the blip instead.
        if ((e as ApiError).status === 404) return [];
        throw e;
      }
    },
    enabled,
    refetchInterval: (q) => (document.visibilityState === "visible" ? 12_000 : false),
    refetchIntervalInBackground: false,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  return {
    invitations: query.data ?? [],
    loading: enabled && query.isLoading,
    refetch: () => query.refetch(),
  };
}

/**
 * The list endpoint returns `voucher_link: null`. A link arrives only in the
 * invite and resend responses, which write it into this cache, and this merge
 * keeps it across the 12s poll. No screen shows it any more: the "Copy link"
 * button that read it was removed, because it handed the applicant a working
 * voucher link. Do not render it.
 */
export function keepKnownLinks(
  fresh: VoucherInvitation[],
  cached: VoucherInvitation[] | undefined,
): VoucherInvitation[] {
  if (!cached?.length) return fresh;
  const known = new Map(cached.map((inv) => [inv.invitation_id, inv.voucher_link]));
  return fresh.map((inv) =>
    inv.voucher_link ? inv : { ...inv, voucher_link: known.get(inv.invitation_id) ?? null },
  );
}
